import { useEffect } from "react";
import { useSiteSettings } from "../contexts/SiteSettingsContext";
import { useLocation } from "react-router-dom";
import { ALL_LANGS, OG_LOCALE, localeFromPath, localizedPath, stripLocale } from "../lib/locale";

/**
 * SEO component leveraging React 19's native head element hoisting.
 * Renders <title>, <meta>, <link rel="canonical">, JSON-LD scripts directly.
 */
export default function SEOHead({
    title,
    description,
    canonical,
    image = "https://adrise-agency.com/og-image.jpg",
    type = "website",
    keywords = [],
    jsonLd = null,
    article = null,
    noIndex = false,
    alternates = [],
}) {
    const dedupe = (selector) => {
        const all = document.head.querySelectorAll(selector);
        all.forEach((n, i) => i > 0 && n.remove());
        return all[0] || null;
    };

    const upsertMetaByName = (name, content) => {
        if (!content) return;
        let el = dedupe(`meta[name="${name}"]`);
        if (!el) {
            el = document.createElement("meta");
            el.setAttribute("name", name);
            document.head.appendChild(el);
        }
        el.setAttribute("content", content);
    };

    const upsertMetaByProperty = (property, content) => {
        if (!content) return;
        let el = dedupe(`meta[property="${property}"]`);
        if (!el) {
            el = document.createElement("meta");
            el.setAttribute("property", property);
            document.head.appendChild(el);
        }
        el.setAttribute("content", content);
    };

    const upsertLinkRel = (rel, href) => {
        if (!href) return;
        let el = dedupe(`link[rel="${rel}"]`);
        if (!el) {
            el = document.createElement("link");
            el.setAttribute("rel", rel);
            document.head.appendChild(el);
        }
        el.setAttribute("href", href);
    };

    const { settings } = useSiteSettings();
    const { pathname } = useLocation();
    const currentLang = localeFromPath(pathname) || "en";
    const basePath = stripLocale(pathname);
    const SITE = "https://adrise-agency.com";
    let canonicalPath = basePath;
    if (canonical) {
        try { canonicalPath = stripLocale(new URL(canonical, SITE).pathname); } catch { canonicalPath = basePath; }
    }
    const localizedCanonical = `${SITE}${localizedPath(currentLang, canonicalPath)}`;

    // hreflang alternates for /tr /es /pt /ru + x-default (English, unprefixed)
    useEffect(() => {
        document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
        if (noIndex) return;
        const add = (hreflang, href) => {
            const el = document.createElement("link");
            el.setAttribute("rel", "alternate");
            el.setAttribute("hreflang", hreflang);
            el.setAttribute("href", href);
            document.head.appendChild(el);
        };
        ALL_LANGS.forEach((l) => add(l, `${SITE}${localizedPath(l, basePath)}`));
        add("x-default", `${SITE}${basePath}`);
        document.documentElement.lang = currentLang;
    }, [basePath, currentLang, noIndex]);

    useEffect(() => {
        if (settings?.seo_google_site_verification) upsertMetaByName("google-site-verification", settings.seo_google_site_verification);
        if (settings?.seo_bing_site_verification) upsertMetaByName("msvalidate.01", settings.seo_bing_site_verification);
        if (settings?.seo_yandex_verification) upsertMetaByName("yandex-verification", settings.seo_yandex_verification);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings?.seo_google_site_verification, settings?.seo_bing_site_verification, settings?.seo_yandex_verification]);

    // Update document title & meta on mount/update (works for SSR-less SPA crawlers)
    useEffect(() => {
        if (title) document.title = title;
        upsertMetaByName(
            "robots",
            noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large",
        );
        if (description) upsertMetaByName("description", description);
        if (keywords.length > 0) {
            upsertMetaByName("keywords", keywords.join(", "));
        }
        upsertLinkRel("canonical", localizedCanonical);

        upsertMetaByProperty("og:type", type);
        if (title) upsertMetaByProperty("og:title", title);
        if (description) upsertMetaByProperty("og:description", description);
        upsertMetaByProperty("og:url", localizedCanonical);
        if (image) upsertMetaByProperty("og:image", image);
        upsertMetaByProperty("og:locale", OG_LOCALE[currentLang] || "en_US");
        upsertMetaByProperty("og:site_name", "Adrise");

        upsertMetaByName("twitter:card", "summary_large_image");
        upsertMetaByName("twitter:site", "@adrise");
        if (title) upsertMetaByName("twitter:title", title);
        if (description) upsertMetaByName("twitter:description", description);
        if (image) upsertMetaByName("twitter:image", image);

        if (article?.publishedTime) {
            upsertMetaByProperty("article:published_time", article.publishedTime);
        }
        if (article?.author) {
            upsertMetaByProperty("article:author", article.author);
        }
        if (article?.section) {
            upsertMetaByProperty("article:section", article.section);
        }
    }, [title, localizedCanonical]);

    return (
        <>
            {/* JSON-LD */}
            {Array.isArray(jsonLd)
                ? jsonLd.map((entry, idx) => (
                      <script
                          key={`jsonld-${idx}`}
                          type="application/ld+json"
                          dangerouslySetInnerHTML={{
                              __html: JSON.stringify(entry),
                          }}
                      />
                  ))
                : jsonLd && (
                      <script
                          type="application/ld+json"
                          dangerouslySetInnerHTML={{
                              __html: JSON.stringify(jsonLd),
                          }}
                      />
                  )}
        </>
    );
}
